import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of, BehaviorSubject } from 'rxjs';
import { map, catchError, tap } from 'rxjs/operators';
import { environment } from 'src/environments/environment';

/**
 * Interface para la respuesta de geolocalización
 */
export interface GeoLocationResponse {
  ip: string;
  countryCode: string;
  countryName: string;
  city?: string;
  timezone?: string;
  latitude?: number;
  longitude?: number;
  network?: {
    asn: string;
    organization: string;
    country: string;
  };
}

/**
 * Interface para la respuesta simplificada (solo código de país)
 */
export interface CountryCodeResponse {
  countryCode: string;
  countryName: string;
}

/**
 * Servicio para obtener la geolocalización del visitante.
 * Detecta el país del usuario basándose en su IP pública.
 */
@Injectable({
  providedIn: 'root'
})
export class GeolocationService {

  // Cache para evitar llamadas repetidas
  private cachedGeoLocation: GeoLocationResponse | null = null;
  private countryCode$ = new BehaviorSubject<string>('PE'); // Default: Perú

  constructor(private httpClient: HttpClient) { }

  /**
   * Obtiene la geolocalización completa del visitante.
   * Incluye IP, país, ciudad, timezone, coordenadas y red.
   */
  getGeoLocation(): Observable<GeoLocationResponse> {
    // Si ya tenemos datos en cache, retornarlos
    if (this.cachedGeoLocation) {
      return of(this.cachedGeoLocation);
    }

    const url = `${environment.urlBase}${environment.geoLocationCountry}`;

    return this.httpClient.get<GeoLocationResponse>(url).pipe(
      tap(data => {
        this.cachedGeoLocation = data;
        if (data.countryCode) {
          this.countryCode$.next(data.countryCode);
        }
        // console.log('Geolocalización obtenida:', data.countryName, '(' + data.countryCode + ')');
      }),
      catchError(error => {
        console.error('Error al obtener geolocalización:', error);
        // Retornar Perú por defecto en caso de error
        const defaultResponse: GeoLocationResponse = {
          ip: '',
          countryCode: 'PE',
          countryName: 'Peru'
        };
        return of(defaultResponse);
      })
    );
  }

  /**
   * Obtiene solo el código de país del visitante (respuesta simplificada).
   * Útil para aplicaciones que solo necesitan mostrar la bandera.
   */
  getCountryCode(): Observable<CountryCodeResponse> {
    // Si ya tenemos datos en cache, retornar solo el código
    if (this.cachedGeoLocation) {
      return of({
        countryCode: this.cachedGeoLocation.countryCode,
        countryName: this.cachedGeoLocation.countryName
      });
    }

    const url = `${environment.urlBase}${environment.geoLocationCountryCode}`;

    return this.httpClient.get<CountryCodeResponse>(url).pipe(
      tap(data => {
        if (data.countryCode) {
          this.countryCode$.next(data.countryCode);
        }
        // console.log('País detectado:', data.countryName, '(' + data.countryCode + ')');
      }),
      catchError(error => {
        console.error('Error al obtener código de país:', error);
        // Retornar Perú por defecto en caso de error
        return of({
          countryCode: 'PE',
          countryName: 'Peru'
        });
      })
    );
  }

  /**
   * Observable para suscribirse a cambios en el código de país.
   * Útil para componentes que necesitan reaccionar cuando se detecta el país.
   */
  get countryCodeObservable(): Observable<string> {
    return this.countryCode$.asObservable();
  }

  /**
   * Obtiene el código de país actual (sincrónico).
   * Retorna 'PE' si no se ha detectado aún.
   */
  get currentCountryCode(): string {
    return this.countryCode$.value;
  }

  /**
   * Limpia la cache de geolocalización.
   * Útil si necesitas forzar una nueva consulta.
   */
  clearCache(): void {
    this.cachedGeoLocation = null;
  }

  /**
   * Obtiene la URL de la bandera para un código de país.
   * Utiliza el formato ISO 3166-1 alpha-2 en minúsculas.
   */
  getFlagUrl(countryCode: string): string {
    if (!countryCode) {
      countryCode = 'PE';
    }
    return `/assets/image/flags/${countryCode.toLowerCase()}.svg`;
  }

  /**
   * Verifica si el visitante está en Perú.
   */
  isFromPeru(): boolean {
    return this.countryCode$.value === 'PE';
  }
}
